import { useState, useMemo, useEffect } from 'react';
import { 
  ChevronDown, 
  ExternalLink, 
  BookOpen, 
  HelpCircle, 
  ArrowLeft,
  Search,
  Sparkles,
  Clock,
  CheckCircle,
  AlertCircle,
  Zap,
  MessageCircle,
  Phone,
  Mail,
  Play,
  SkipForward,
  SkipBack,
  Settings,
  ChevronUp,
  Link2,
  Check
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { Logo } from '../components/Logo';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: 'general' | 'technical' | 'billing' | 'troubleshooting' | 'third-party' | 'setup' | 'customization' | 'wizard';
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedTime: string;
  tags: string[];
  walkthrough?: {
    title: string;
    description: string;
    steps: string[];
    screenshots?: string[];
  };
  relatedQuestions?: string[];
  lastUpdated?: string;
}

// Updated FAQ data for the new multi-step wizard
const faqData: FAQItem[] = [
  {
    id: 'getting-started',
    question: 'How do I get started with my application setup?',
    answer: 'Getting started is easy! Our multi-step wizard guides you through the application setup process in 6 logical steps. Each step focuses on a specific aspect of your app configuration, and you can see your progress in real-time with our dual progress bars and step indicators.',
    category: 'setup',
    difficulty: 'beginner',
    estimatedTime: '15-20 minutes',
    tags: ['onboarding', 'setup', 'first-time', 'wizard', 'progress'],
    walkthrough: {
      title: 'Complete Application Setup Guide',
      description: 'A comprehensive walkthrough covering every step of your application setup process using our multi-step wizard.',
              steps: [
        'Step 1: Basic Information - Enter your app name, upload logo and icon, choose brand colors',
        'Step 2: App Store Information - Provide contact details and legal URLs',
        'Step 3: Shop Configuration - Set up e-commerce functionality (if needed)',
        'Step 4: Testing & Integration - Configure VersiRent integration and testing',
        'Step 5: Apple Developer Setup - Complete Apple account requirements',
        'Step 6: Notification Preferences - Choose how you want to receive updates',
        'Review your progress using the dual progress bars',
        'Navigate between steps using the step indicators',
        'Use the "Save Progress" button to preserve your work',
        'Submit your application when all steps are complete'
        ],
              screenshots: [
        '/screenshots/wizard/01-step-1-basic-information.png', // Step 1: Basic Information
        '/screenshots/wizard/02-step-2-app-store-information.png', // Step 2: App Store Information
        '/screenshots/wizard/03-step-3-shop-configuration.png', // Step 3: Shop Configuration
        '/screenshots/wizard/04-step-4-testing-integration.png', // Step 4: Testing & Integration
        '/screenshots/wizard/05-step-5-apple-developer-setup.png', // Step 5: Apple Developer Setup
        '/screenshots/wizard/06-step-6-notification-preferences.png', // Step 6: Notification Preferences
        '/screenshots/wizard/01a-progress-bars.png', // Step 7: Progress tracking
        '/screenshots/wizard/08-step-indicators-navigation.png', // Step 8: Step navigation (shows step indicators clearly)
        '/screenshots/wizard/07a-navigation-controls.png', // Step 9: Navigation controls
        '/screenshots/wizard/07b-progress-with-fields-filled.png' // Step 10: Complete wizard
      ]
    },
    relatedQuestions: ['wizard-progress-tracking', 'wizard-navigation', 'wizard-auto-save'],
    lastUpdated: '2025-01-08'
  },
  {
    id: 'wizard-progress-tracking',
    question: 'How does the progress tracking work in the wizard?',
    answer: 'The wizard features dual progress bars that show both your overall completion across all steps and your current step completion. The progress bars update in real-time as you fill out fields, giving you accurate feedback on your progress. Step indicators also show completion percentages for each step.',
    category: 'wizard',
    difficulty: 'beginner',
    estimatedTime: '2 minutes',
    tags: ['wizard', 'progress', 'tracking', 'completion', 'feedback'],
    walkthrough: {
      title: 'Understanding Progress Tracking',
      description: 'Learn how to use the dual progress bars and step indicators to track your completion.',
      steps: [
        'Look at the green "Overall Progress" bar at the top',
        'Check the blue "Current Step Progress" bar below it',
        'Watch the step indicators show completion percentages',
        'Notice how progress updates as you fill out fields',
        'Use the progress to identify which steps need attention',
        'Navigate to any step by clicking its indicator'
      ],
      screenshots: [
        '/screenshots/wizard/01a-progress-bars.png',
        '/screenshots/wizard/01b-step-indicators.png',
        '/screenshots/wizard/07b-progress-with-fields-filled.png'
      ]
    },
    relatedQuestions: ['wizard-getting-started', 'wizard-navigation'],
    lastUpdated: '2025-01-08'
  },
  {
    id: 'wizard-navigation',
    question: 'How do I navigate between steps in the wizard?',
    answer: 'You can navigate between steps in several ways: 1) Use the Previous/Next buttons at the bottom of each step, 2) Click directly on any step indicator at the top, 3) The wizard will automatically validate each step before allowing you to proceed. You can also use the "Save Progress" button to preserve your work.',
    category: 'wizard',
    difficulty: 'beginner',
    estimatedTime: '1 minute',
    tags: ['wizard', 'navigation', 'steps', 'validation', 'save'],
    walkthrough: {
      title: 'Wizard Navigation Guide',
      description: 'Learn all the ways to navigate through the wizard steps.',
      steps: [
        'Use the "Next" button to proceed to the next step',
        'Use the "Previous" button to go back to the previous step',
        'Click any step number in the step indicators to jump directly to that step',
        'Use the "Save Progress" button to save your current work',
        'Notice that the "Next" button is disabled until the current step is complete',
        'Check the step completion percentages to see what needs to be filled'
      ],
      screenshots: [
        '/screenshots/wizard/01b-step-indicators.png',
        '/screenshots/wizard/07a-navigation-controls.png'
      ]
    },
    relatedQuestions: ['wizard-getting-started', 'wizard-progress-tracking'],
    lastUpdated: '2025-01-08'
  },
  {
    id: 'wizard-auto-save',
    question: 'Does the wizard automatically save my progress?',
    answer: 'Yes! The wizard automatically saves your progress every 30 seconds as you work. You can also manually save using the "Save Progress" button. This means you can safely close your browser and return later without losing your work. The last saved time is displayed next to the save button.',
    category: 'wizard',
    difficulty: 'beginner',
    estimatedTime: '1 minute',
    tags: ['wizard', 'auto-save', 'save', 'progress', 'safety'],
    walkthrough: {
      title: 'Auto-Save and Manual Save',
      description: 'Understand how the wizard saves your progress automatically and manually.',
      steps: [
        'Start filling out fields in any step',
        'Notice the auto-save happens every 30 seconds',
        'Look for the "Last saved" timestamp below the save button',
        'Use the "Save Progress" button for manual saves',
        'Close your browser and return later - your progress is preserved',
        'Continue from where you left off'
      ],
      screenshots: [
        '/screenshots/wizard/07a-navigation-controls.png'
      ]
    },
    relatedQuestions: ['wizard-getting-started', 'wizard-navigation'],
    lastUpdated: '2025-01-08'
  },
  {
    id: 'step-1-basic-information',
    question: 'What information do I need for Step 1: Basic Information?',
    answer: 'Step 1 focuses on your app\'s core identity. You\'ll need: your application name, company logo (PNG/JPG/SVG, up to 5MB), app icon (or use "Same as logo"), primary brand color, company description (optional), and copyright message (optional, defaults to current year).',
    category: 'setup',
    difficulty: 'beginner',
    estimatedTime: '5-7 minutes',
    tags: ['step-1', 'basic-info', 'logo', 'icon', 'colors', 'branding'],
    walkthrough: {
      title: 'Step 1: Basic Information Complete Guide',
      description: 'Detailed walkthrough for completing the basic information step.',
      steps: [
        'Enter your application name (this will appear in app stores)',
        'Upload your company logo (PNG, JPG, or SVG format, max 5MB)',
        'Choose whether to use your logo as the app icon or upload a separate icon',
        'Select your primary brand color using the color picker',
        'Add a company description (optional but recommended)',
        'Set your copyright message (optional, defaults to current year)',
        'Watch the progress bar update as you complete each field'
      ],
      screenshots: [
        '/screenshots/wizard/01-step-1-basic-information.png',
        '/screenshots/wizard/01c-basic-information-form.png'
      ]
    },
    relatedQuestions: ['logo-upload', 'app-icon-upload', 'color-selection'],
    lastUpdated: '2025-01-08'
  },
  {
    id: 'step-2-app-store-information',
    question: 'What information do I need for Step 2: App Store Information?',
    answer: 'Step 2 collects the information that will be displayed in your app store listing. You\'ll need: your app store contact email, app store contact phone number, terms and conditions URL, and privacy policy URL. All of these are required for app store approval.',
    category: 'setup',
    difficulty: 'beginner',
    estimatedTime: '3-5 minutes',
    tags: ['step-2', 'app-store', 'contact', 'legal', 'urls'],
    walkthrough: {
      title: 'Step 2: App Store Information Guide',
      description: 'Complete guide for providing app store listing information.',
      steps: [
        'Enter your app store contact email (visible to users)',
        'Provide your app store contact phone number',
        'Add your terms and conditions URL (must be live and accessible)',
        'Add your privacy policy URL (must be live and accessible)',
        'Ensure all URLs start with https:// for security',
        'Verify that your legal pages are accessible before submitting'
      ],
      screenshots: [
        '/screenshots/wizard/02-step-2-app-store-information.png',
        '/screenshots/wizard/02a-app-store-form.png'
      ]
    },
    relatedQuestions: ['legal-requirements', 'app-store-requirements'],
    lastUpdated: '2025-01-08'
  },
  {
    id: 'step-3-shop-configuration',
    question: 'What is Step 3: Shop Configuration about?',
    answer: 'Step 3 is for configuring e-commerce functionality. You can choose to use your own website for shop features (with a custom shop URL) or use the built-in shop functionality. For IdealOne apps, you can also set a secondary brand color. This step is optional if you don\'t need shop features.',
    category: 'setup',
    difficulty: 'intermediate',
    estimatedTime: '2-3 minutes',
    tags: ['step-3', 'shop', 'e-commerce', 'website', 'configuration'],
    walkthrough: {
      title: 'Step 3: Shop Configuration Options',
      description: 'Learn about the different shop configuration options available.',
      steps: [
        'Decide if you need shop/e-commerce functionality',
        'If yes: Check "Use my website for shop features"',
        'If using your website: Enter your custom shop URL (must be HTTPS)',
        'For IdealOne: Optionally set a secondary brand color',
        'If no shop needed: Leave the checkbox unchecked',
        'The custom shop URL field only appears when needed'
      ],
      screenshots: [
        '/screenshots/wizard/03-step-3-shop-configuration.png',
        '/screenshots/wizard/03a-shop-configuration-form.png'
      ]
    },
    relatedQuestions: ['e-commerce-setup', 'custom-shop-url'],
    lastUpdated: '2025-01-08'
  },
  {
    id: 'step-4-testing-integration',
    question: 'What do I need for Step 4: Testing & Integration?',
    answer: 'Step 4 handles testing setup and third-party integration. You\'ll need: VersiRent test account username and password, tester email addresses (comma-separated), and the signed Google Play Brand Asset Agreement PDF. This step ensures your app can be properly tested and integrated.',
    category: 'technical',
    difficulty: 'intermediate',
    estimatedTime: '5-7 minutes',
    tags: ['step-4', 'testing', 'versirent', 'integration', 'google-play'],
    walkthrough: {
      title: 'Step 4: Testing & Integration Setup',
      description: 'Complete guide for setting up testing and third-party integration.',
      steps: [
        'Enter your VersiRent test account username',
        'Enter your VersiRent test account password',
        'Add tester email addresses (separate multiple emails with commas)',
        'Upload the signed Google Play Brand Asset Agreement PDF',
        'Ensure the PDF is signed and complete before uploading',
        'Verify that your VersiRent test account credentials are correct'
      ],
      screenshots: [
        '/screenshots/wizard/04-step-4-testing-integration.png',
        '/screenshots/wizard/04a-versirent-integration.png',
        '/screenshots/wizard/04b-app-testing.png'
      ]
    },
    relatedQuestions: ['versirent-integration', 'testing-setup', 'google-play-agreement'],
    lastUpdated: '2025-01-08'
  },
  {
    id: 'step-5-apple-developer-setup',
    question: 'What is required for Step 5: Apple Developer Setup?',
    answer: 'Step 5 handles all Apple-specific requirements. You\'ll need: your Apple Developer account name (exact name from App Store Connect), your Apple Team ID (10-character string), confirmation that all required team members have been added to your App Store Connect account, and confirmation that Team Key functionality has been enabled.',
    category: 'technical',
    difficulty: 'advanced',
    estimatedTime: '10-15 minutes',
    tags: ['step-5', 'apple', 'developer', 'team-id', 'invites', 'team-key'],
    walkthrough: {
      title: 'Step 5: Apple Developer Setup Complete Guide',
      description: 'Detailed walkthrough for completing Apple Developer requirements.',
      steps: [
        'Enter your exact Apple Developer account name (as it appears in App Store Connect)',
        'Provide your Apple Team ID (10-character string from App Store Connect)',
        'Confirm that mwhorey@idealss.com has been added (Admin role)',
        'Confirm that nmore@idealss.com has been added (Admin role)',
        'Confirm that dev@idealss.com has been added (Admin role)',
        'Confirm that cstack@idealss.com has been added (Admin role)',
        'Confirm that dscarafiotti@hotmail.com has been added (Sales role)',
        'Confirm that Team Key functionality has been enabled in your Apple Developer account',
        'All checkboxes must be checked to complete this step'
      ],
      screenshots: [
        '/screenshots/wizard/05-step-5-apple-developer-setup.png',
        '/screenshots/wizard/05a-apple-account-info.png',
        '/screenshots/wizard/05b-apple-account-invites.png',
        '/screenshots/wizard/05c-team-key-functionality.png'
      ]
    },
    relatedQuestions: ['apple-team-id', 'apple-account-invites', 'team-key-setup'],
    lastUpdated: '2025-01-08'
  },
  {
    id: 'step-6-notification-preferences',
    question: 'What are the notification preferences in Step 6?',
    answer: 'Step 6 lets you choose how you want to receive updates about your project. You can opt-in to email notifications (with a custom notification email) and/or SMS notifications (with a phone number). Both are optional - you can choose one, both, or neither. This helps us keep you informed about your project status.',
    category: 'setup',
    difficulty: 'beginner',
    estimatedTime: '2-3 minutes',
    tags: ['step-6', 'notifications', 'email', 'sms', 'preferences'],
    walkthrough: {
      title: 'Step 6: Notification Preferences Setup',
      description: 'Guide for setting up your notification preferences.',
      steps: [
        'Decide if you want email notifications',
        'If yes: Check "Enable Email Notifications" and enter your notification email',
        'Decide if you want SMS notifications',
        'If yes: Check "Enable SMS Notifications" and enter your phone number',
        'Use international format for phone numbers (e.g., +1 555 123 4567)',
        'You can choose email only, SMS only, both, or neither',
        'These preferences can be changed later if needed'
      ],
      screenshots: [
        '/screenshots/wizard/06-step-6-notification-preferences.png',
        '/screenshots/wizard/06a-communication-preferences.png'
      ]
    },
    relatedQuestions: ['notification-setup', 'email-notifications', 'sms-notifications'],
    lastUpdated: '2025-01-08'
  },
  {
    id: 'wizard-mobile-responsive',
    question: 'Does the wizard work on mobile devices?',
    answer: 'Yes! The wizard is fully responsive and works great on mobile devices, tablets, and desktops. The step indicators adapt to smaller screens, and all form fields are touch-friendly. You can complete the entire setup process on your phone or tablet.',
    category: 'wizard',
    difficulty: 'beginner',
    estimatedTime: '1 minute',
    tags: ['wizard', 'mobile', 'responsive', 'tablet', 'touch'],
    walkthrough: {
      title: 'Using the Wizard on Mobile Devices',
      description: 'Tips for using the wizard on mobile and tablet devices.',
      steps: [
        'Open the wizard on your mobile device or tablet',
        'Notice how the step indicators adapt to the smaller screen',
        'Use touch gestures to navigate between steps',
        'All form fields are optimized for touch input',
        'The progress bars remain visible and functional',
        'Auto-save works the same on mobile devices'
      ],
      screenshots: [
        '/screenshots/wizard/08-mobile-wizard-view.png',
        '/screenshots/wizard/08a-mobile-step-indicators.png',
        '/screenshots/wizard/09-tablet-wizard-view.png'
      ]
    },
    relatedQuestions: ['wizard-getting-started', 'wizard-navigation'],
    lastUpdated: '2025-01-08'
  },
  {
    id: 'wizard-differences-versirent-idealone',
    question: 'Are there differences between VersiRent and IdealOne wizards?',
    answer: 'The wizard structure is the same for both app types, but there are some differences in the fields. IdealOne apps have an additional secondary brand color field in Step 3 (Shop Configuration), while VersiRent apps don\'t. All other steps and fields are identical between the two app types.',
    category: 'wizard',
    difficulty: 'beginner',
    estimatedTime: '1 minute',
    tags: ['wizard', 'versirent', 'idealone', 'differences', 'app-types'],
    walkthrough: {
      title: 'VersiRent vs IdealOne Wizard Differences',
      description: 'Understanding the differences between the two app type wizards.',
      steps: [
        'Both app types use the same 6-step wizard structure',
        'Steps 1, 2, 4, 5, and 6 are identical for both app types',
        'Step 3 (Shop Configuration) has one difference:',
        '  - IdealOne: Has secondary brand color field',
        '  - VersiRent: No secondary brand color field',
        'All other fields, validation, and functionality are the same',
        'The progress tracking works identically for both app types'
      ],
      screenshots: [
        '/screenshots/wizard/01-step-1-basic-information.png',
        '/screenshots/wizard/10-idealone-wizard-step-1.png',
        '/screenshots/wizard/10a-idealone-shop-configuration.png'
      ]
    },
    relatedQuestions: ['wizard-getting-started', 'step-3-shop-configuration'],
    lastUpdated: '2025-01-08'
  },
  {
    id: 'logo-upload',
    question: 'What are the requirements for uploading my company logo?',
    answer: 'Your logo should be in PNG, JPG, or SVG format with a maximum file size of 5MB. For best results, use a high-resolution image with a transparent background. We recommend using PNG format for the best quality and transparency support.',
    category: 'technical',
    difficulty: 'beginner',
    estimatedTime: '2-3 minutes',
    tags: ['logo', 'upload', 'file-format', 'requirements'],
    walkthrough: {
      title: 'Logo Upload Best Practices',
      description: 'Learn how to prepare and upload your logo for the best results.',
              steps: [
          'Prepare your logo file (PNG, JPG, or SVG format)',
          'Ensure the file size is under 5MB',
          'For PNG/JPG: Use a transparent background if possible',
          'For SVG: Ensure the file is properly formatted and optimized',
        'Click the upload area or drag and drop your file',
        'Wait for the upload to complete (you\'ll see a green checkmark)',
        'If upload fails, check file size and format requirements'
        ],
      screenshots: [
        '/screenshots/wizard/01-step-1-basic-information.png', // Step 1: wizard step 1 overview
        '/screenshots/wizard/01c-basic-information-form.png', // Step 2: basic information form
        '/screenshots/wizard/01a-progress-bars.png', // Step 3: progress bars
        '/screenshots/wizard/01b-step-indicators.png', // Step 4: step indicators
        '/screenshots/wizard/07a-navigation-controls.png', // Step 5: navigation controls
        '/screenshots/wizard/07b-progress-with-fields-filled.png', // Step 6: progress with fields filled
        '/screenshots/wizard/08-mobile-wizard-view.png', // Step 7: mobile wizard view
      ]
    },
    relatedQuestions: ['file-format-requirements', 'upload-troubleshooting', 'image-optimization'],
    lastUpdated: '2025-09-03'
  },
  {
    id: 'app-icon-upload',
    question: 'How do I upload my app icon and what are the requirements?',
    answer: 'Your app icon should be a square image (1024x1024 pixels recommended) in PNG format. You can either upload a custom icon or use the "Same as logo" option if your logo works well as an app icon.',
    category: 'technical',
    difficulty: 'beginner',
    estimatedTime: '3-5 minutes',
    tags: ['app-icon', 'upload', 'requirements', 'same-as-logo'],
    walkthrough: {
      title: 'App Icon Upload Guide',
      description: 'Step-by-step instructions for uploading your app icon.',
      steps: [
        'Decide whether to upload a custom icon or use "Same as logo"',
        'If uploading custom: Prepare a 1024x1024 PNG image',
        'If using "Same as logo": Check the "Same as logo" checkbox',
        'For custom upload: Click the upload area and select your file',
        'Wait for processing to complete',
        'Preview how your icon will look on different devices',
        'Make adjustments if needed'
      ],
      screenshots: [
        '/screenshots/wizard/01-step-1-basic-information.png', // Step 1: wizard step 1 overview
        '/screenshots/wizard/01c-basic-information-form.png', // Step 2: basic information form with app icon
        '/screenshots/wizard/01a-progress-bars.png', // Step 3: progress bars
        '/screenshots/wizard/01b-step-indicators.png', // Step 4: step indicators
        '/screenshots/wizard/07a-navigation-controls.png', // Step 5: navigation controls
        '/screenshots/wizard/07b-progress-with-fields-filled.png', // Step 6: progress with fields filled
        '/screenshots/wizard/08-mobile-wizard-view.png', // Step 7: mobile wizard view
      ]
    },
    relatedQuestions: ['logo-upload', 'image-requirements', 'app-store-guidelines'],
    lastUpdated: '2025-09-03'
  },
  {
    id: 'color-selection',
    question: 'How do I choose the right brand color for my app?',
    answer: 'Your brand color will be used throughout your app for buttons, links, and accent elements. Choose a color that represents your brand and provides good contrast for accessibility.',
    category: 'customization',
    difficulty: 'beginner',
    estimatedTime: '2-3 minutes',
    tags: ['brand-color', 'customization', 'accessibility', 'color-picker'],
    walkthrough: {
      title: 'Brand Color Selection Guide',
      description: 'Choose the perfect brand color for your app.',
      steps: [
        'Click on the color picker field',
        'Use the color wheel or enter a hex code',
        'Consider accessibility - ensure good contrast with white text',
        'Preview how the color looks in the interface',
        'Test with both light and dark backgrounds',
        'Save your selection when satisfied'
      ],
      screenshots: [
        '/screenshots/wizard/01-step-1-basic-information.png', // Step 1: wizard step 1 overview
        '/screenshots/wizard/01c-basic-information-form.png', // Step 2: basic information form with color picker
        '/screenshots/wizard/01a-progress-bars.png', // Step 3: progress bars
        '/screenshots/wizard/01b-step-indicators.png', // Step 4: step indicators
        '/screenshots/wizard/07a-navigation-controls.png', // Step 5: navigation controls
        '/screenshots/wizard/07b-progress-with-fields-filled.png', // Step 6: progress with fields filled
      ]
    },
    relatedQuestions: ['accessibility-guidelines', 'brand-consistency', 'color-theory'],
    lastUpdated: '2025-09-03'
  },
  {
    id: 'progress-tracking',
    question: 'How can I track my progress through the setup process?',
    answer: 'The Progress Summary sidebar on the right shows your completion status for each required field. Green checkmarks indicate completed fields, and you\'ll see a "Ready to Submit" button when everything is complete.',
    category: 'setup',
    difficulty: 'beginner',
    estimatedTime: '1-2 minutes',
    tags: ['progress', 'tracking', 'completion', 'sidebar'],
    walkthrough: {
      title: 'Progress Tracking Guide',
      description: 'Monitor your setup progress and know when you\'re ready to submit.',
      steps: [
        'Look at the Progress Summary sidebar on the right',
        'Check which fields show green checkmarks (completed)',
        'Identify any fields that still need attention',
        'Complete missing required fields',
        'Watch for the "Ready to Submit" button to appear',
        'Click submit when all required fields are complete'
      ],
      screenshots: [
        '/screenshots/wizard/01a-progress-bars.png', // Step 1: dual progress bars
        '/screenshots/wizard/01b-step-indicators.png', // Step 2: step indicators with progress
        '/screenshots/wizard/07b-progress-with-fields-filled.png', // Step 3: progress with fields filled
        '/screenshots/wizard/01-step-1-basic-information.png', // Step 4: wizard overview
        '/screenshots/wizard/07a-navigation-controls.png', // Step 5: navigation controls
        '/screenshots/wizard/08-mobile-wizard-view.png', // Step 6: mobile wizard view
      ]
    },
    relatedQuestions: ['required-fields', 'submission-process', 'completion-checklist'],
    lastUpdated: '2025-09-03'
  },
  {
    id: 'submission-process',
    question: 'What happens after I submit my application for provisioning?',
    answer: 'After submission, your application will be reviewed by our team. We\'ll provision your app with the necessary configurations and contact you if we need any additional information. The process typically takes 1-2 business days.',
    category: 'setup',
    difficulty: 'beginner',
    estimatedTime: '5-10 minutes',
    tags: ['submission', 'provisioning', 'review-process', 'timeline'],
    walkthrough: {
      title: 'Submission and Provisioning Process',
      description: 'What to expect after submitting your application.',
              steps: [
        'Ensure all required fields are complete',
        'Click "Submit for Provisioning" button',
        'Review the confirmation message',
        'Check your email for submission confirmation',
        'Wait for our team to review your application',
        'Respond to any follow-up questions promptly',
        'Receive notification when provisioning is complete'
        ],
      screenshots: [
        '/screenshots/wizard/07b-progress-with-fields-filled.png', // Step 1: wizard with all fields complete
        '/screenshots/wizard/07a-navigation-controls.png', // Step 2: navigation controls with submit button
        '/screenshots/wizard/01a-progress-bars.png', // Step 3: progress bars showing completion
        '/screenshots/wizard/01b-step-indicators.png', // Step 4: step indicators showing all complete
        '/screenshots/wizard/06-step-6-notification-preferences.png', // Step 5: notification preferences step
        '/screenshots/wizard/08-mobile-wizard-view.png', // Step 6: mobile wizard view
        '/screenshots/wizard/09-tablet-wizard-view.png', // Step 7: tablet wizard view
      ]
    },
    relatedQuestions: ['provisioning-timeline', 'review-process', 'follow-up-questions'],
    lastUpdated: '2025-09-03'
  },
  {
    id: 'mobile-responsive',
    question: 'How does the onboarding portal work on mobile devices?',
    answer: 'The onboarding portal is fully responsive and works seamlessly on all devices. On mobile, the interface adapts to provide an optimal experience with touch-friendly controls and mobile-optimized layouts.',
    category: 'technical',
    difficulty: 'beginner',
    estimatedTime: '1 minute',
    tags: ['mobile', 'responsive', 'touch-friendly', 'cross-platform'],
    walkthrough: {
      title: 'Mobile Experience Guide',
      description: 'Using the onboarding portal on mobile devices.',
      steps: [
        'Access the portal from your mobile browser',
        'The interface automatically adapts to your screen size',
        'Use touch gestures to navigate and interact',
        'Upload files using your device\'s file picker',
        'All features work the same as on desktop',
        'Progress is saved across all devices'
      ],
      screenshots: [
        '/screenshots/wizard/08-mobile-wizard-view.png', // Step 1: mobile wizard view
        '/screenshots/wizard/08a-mobile-step-indicators.png', // Step 2: mobile step indicators
        '/screenshots/wizard/09-tablet-wizard-view.png', // Step 3: tablet wizard view
        '/screenshots/wizard/01c-basic-information-form.png', // Step 4: form fields on mobile
        '/screenshots/wizard/01a-progress-bars.png', // Step 5: progress bars on mobile
        '/screenshots/wizard/07a-navigation-controls.png', // Step 6: navigation controls on mobile
      ]
    },
    relatedQuestions: ['cross-platform-sync', 'mobile-optimization', 'touch-gestures'],
    lastUpdated: '2025-09-03'
  },
  {
    id: 'file-upload-troubleshooting',
    question: 'What should I do if my file upload fails?',
    answer: 'If file upload fails, check the file size (must be under 5MB), format (PNG, JPG, or SVG), and your internet connection. Clear your browser cache and try again, or contact support if the issue persists.',
    category: 'troubleshooting',
    difficulty: 'intermediate',
    estimatedTime: '3-5 minutes',
    tags: ['troubleshooting', 'file-upload', 'error-resolution', 'support'],
    walkthrough: {
      title: 'File Upload Troubleshooting',
      description: 'Resolve common file upload issues.',
      steps: [
        'Check file size (must be under 5MB)',
        'Verify file format (PNG, JPG, or SVG)',
        'Ensure stable internet connection',
        'Clear browser cache and cookies',
        'Try uploading a different file',
        'Check browser console for error messages',
        'Contact support if issues persist'
      ],
      screenshots: [
        '/screenshots/wizard/01c-basic-information-form.png', // Step 1: wizard form with file upload
        '/screenshots/wizard/01-step-1-basic-information.png', // Step 2: wizard step 1 overview
        '/screenshots/wizard/01a-progress-bars.png', // Step 3: progress bars
        '/screenshots/wizard/01b-step-indicators.png', // Step 4: step indicators
        '/screenshots/wizard/07a-navigation-controls.png', // Step 5: navigation controls
        '/screenshots/wizard/08-mobile-wizard-view.png', // Step 6: mobile wizard view
        '/screenshots/wizard/09-tablet-wizard-view.png', // Step 7: tablet wizard view
      ]
    },
    relatedQuestions: ['file-requirements', 'browser-compatibility', 'support-contact'],
    lastUpdated: '2025-09-03'
  },
  {
    id: 'browser-compatibility',
    question: 'Which browsers are supported for the onboarding process?',
    answer: 'We support all modern browsers including Chrome, Firefox, Safari, and Edge. For the best experience, use the latest version of your browser and ensure JavaScript is enabled.',
    category: 'technical',
    difficulty: 'beginner',
    estimatedTime: '1 minute',
    tags: ['browser-support', 'compatibility', 'javascript', 'modern-browsers'],
    walkthrough: {
      title: 'Browser Compatibility Guide',
      description: 'Ensure your browser is compatible with the onboarding portal.',
      steps: [
        'Use a modern browser (Chrome, Firefox, Safari, or Edge)',
        'Update to the latest browser version',
        'Enable JavaScript in your browser settings',
        'Clear browser cache if experiencing issues',
        'Try a different browser if problems persist',
        'Check our browser compatibility list'
      ],
      screenshots: [
        '/screenshots/wizard/01-step-1-basic-information.png', // Step 1: wizard in modern browser
        '/screenshots/wizard/01a-progress-bars.png', // Step 2: progress bars working
        '/screenshots/wizard/01b-step-indicators.png', // Step 3: step indicators functional
        '/screenshots/wizard/01c-basic-information-form.png', // Step 4: form fields working
        '/screenshots/wizard/07a-navigation-controls.png', // Step 5: navigation controls working
        '/screenshots/wizard/08-mobile-wizard-view.png', // Step 6: mobile compatibility
      ]
    },
    relatedQuestions: ['javascript-requirements', 'browser-updates', 'cross-browser-testing'],
    lastUpdated: '2025-09-03'
  },
  {
    id: 'data-security',
    question: 'How is my data protected during the onboarding process?',
    answer: 'Your data is encrypted in transit and at rest. We use industry-standard security practices and never store sensitive information like passwords. All data is handled in compliance with privacy regulations.',
    category: 'technical',
    difficulty: 'beginner',
    estimatedTime: '1 minute',
    tags: ['security', 'privacy', 'encryption', 'compliance'],
    walkthrough: {
      title: 'Data Security and Privacy',
      description: 'Understanding how your data is protected.',
      steps: [
        'All data is encrypted using SSL/TLS',
        'Files are stored securely in encrypted storage',
        'Sensitive information is never logged',
        'Access is restricted to authorized personnel only',
        'Regular security audits are conducted',
        'Compliance with GDPR and other privacy regulations'
      ],
      screenshots: [
        '/screenshots/wizard/01-step-1-basic-information.png', // Step 1: secure wizard interface
        '/screenshots/wizard/01a-progress-bars.png', // Step 2: secure progress tracking
        '/screenshots/wizard/01b-step-indicators.png', // Step 3: secure step navigation
        '/screenshots/wizard/01c-basic-information-form.png', // Step 4: secure form submission
        '/screenshots/wizard/07a-navigation-controls.png', // Step 5: secure navigation
        '/screenshots/wizard/08-mobile-wizard-view.png', // Step 6: secure mobile access
      ]
    },
    relatedQuestions: ['privacy-policy', 'data-retention', 'security-certifications'],
    lastUpdated: '2025-09-03'
  },
  {
    id: 'apple-developer-setup',
    question: 'How do I set up my Apple Developer account for app submission?',
    answer: 'Setting up your Apple Developer account is essential for app store submission. You\'ll need to ensure proper team access, enable Team Key functionality, and add required team members to your App Store Connect account.',
    category: 'third-party',
    difficulty: 'advanced',
    estimatedTime: '15-30 minutes',
    tags: ['apple', 'developer', 'app-store', 'team-setup', 'advanced'],
    walkthrough: {
      title: 'Apple Developer Account Setup Guide',
      description: 'Complete setup guide for Apple Developer account configuration with step-by-step screenshots.',
      steps: [
        'Log into your Apple Developer account at developer.apple.com',
        'Navigate to your team settings and verify team membership',
        'Add required team members to your App Store Connect account',
        'Enable Team Key functionality in your Apple Developer account',
        'Create API keys in App Store Connect (see Team Key Setup walkthrough)',
        'Verify that all team members have appropriate roles and permissions',
        'Test team access by attempting to access App Store Connect',
        'Document your Apple Developer account name for our records'
      ],
      screenshots: [
        '/screenshots/apple-developer-login.png', // Step 1: Apple Developer login
        '/screenshots/apple-developer-portal.png', // Step 2: Team settings
        '/screenshots/app-store-connect-add-user.png', // Step 3: Add team members
        '/screenshots/app-store-connect-team-keys-enabled.png', // Step 4: Team Key functionality
        '/screenshots/app-store-connect-user-roles.png', // Step 5: Roles and permissions
        '/screenshots/app-store-connect-users-access.png', // Step 6: Test team access
        '/screenshots/apple-developer-name.png', // Step 7: Account name documentation
      ]
    },
    relatedQuestions: ['app-store-submission', 'team-management', 'apple-connect', 'team-key-setup'],
    lastUpdated: '2025-09-03'
  },
  {
    id: 'team-key-setup',
    question: 'How do I enable Team Keys on App Store Connect?',
    answer: 'Team Keys are essential for automated app submission and management. This walkthrough will guide you through enabling Team Key functionality in your Apple Developer account and App Store Connect.',
    category: 'third-party',
    difficulty: 'advanced',
    estimatedTime: '10-20 minutes',
    tags: ['apple', 'team-keys', 'app-store-connect', 'automation', 'advanced'],
    walkthrough: {
      title: 'Team Key Setup on App Store Connect',
      description: 'Complete walkthrough for enabling Team Key functionality in App Store Connect for automated app submission.',
      steps: [
        'Log into App Store Connect at appstoreconnect.apple.com',
        'Navigate to Users and Access section in your account settings',
        'Click on "Keys" tab in the left sidebar',
        'Click "Generate API Key" or "+" button to create a new key',
        'Enter a name for your API key (e.g., "Ideal Mobile App Onboarding")',
        'Select "App Manager" role for the API key',
        'Click "Generate" to create the API key',
        'Download and securely store the .p8 key file',
        'Copy the Key ID and Issuer ID from the key details',
        'Enable Team Key functionality in your Apple Developer account',
        'Verify the key works by testing API access',
        'Mark "Team Key Enabled" as Yes in your onboarding form'
      ],
      screenshots: [
        '/screenshots/app-store-connect-login.png', // Step 1: App Store Connect login
        '/screenshots/app-store-connect-users-access.png', // Step 2: Users and Access section
        '/screenshots/app-store-connect-keys-tab.png', // Step 3: Keys tab
        '/screenshots/app-store-connect-generate-key-button.png', // Step 4: Generate API Key button
        '/screenshots/app-store-connect-key-name-form.png', // Step 5: Key name form
        '/screenshots/app-store-connect-key-role-selection.png', // Step 6: Role selection
        '/screenshots/app-store-connect-key-generated.png', // Step 7: Generated key details
        '/screenshots/app-store-connect-key-download.png', // Step 8: Download .p8 file
        '/screenshots/app-store-connect-key-ids.png', // Step 9: Key ID and Issuer ID
        '/screenshots/apple-developer-team-key-enable.png', // Step 10: Enable in Developer account
        '/screenshots/app-store-connect-key-test.png', // Step 11: Test API access
        '/screenshots/onboarding-form-team-key-enabled.png', // Step 12: Mark enabled in form
      ]
    },
    relatedQuestions: ['apple-developer-setup', 'app-store-submission', 'api-keys'],
    lastUpdated: '2025-01-27'
  },
  {
    id: 'apple-agreement-updates',
    question: 'How do I accept Apple Developer Agreement updates?',
    answer: 'Apple periodically updates their Developer Program License Agreement. You must accept these updates to maintain your developer account and continue using Apple\'s services.',
    category: 'third-party',
    difficulty: 'advanced',
    estimatedTime: '5-10 minutes',
    tags: ['apple', 'developer', 'agreement', 'updates', 'compliance', 'advanced'],
    walkthrough: {
      title: 'Apple Developer Agreement Updates',
      description: 'Guide for accepting Apple Developer Agreement updates with step-by-step screenshots.',
      steps: [
        'Log into your Apple Developer account at developer.apple.com',
        'Look for notification banners or alerts about agreement updates',
        'Click on the agreement update notification',
        'Read through the updated terms and conditions',
        'Review any changes from the previous agreement',
        'Accept the new agreement terms',
        'Confirm your acceptance in the popup dialog',
        'Verify that your account status shows as active'
      ],
      screenshots: [
        '/screenshots/apple-developer-login.png', // Step 1: Apple Developer login
        '/screenshots/apple-developer-agreement-notification.png', // Step 2: Agreement notification
        // Note: No additional screenshots available for agreement review and acceptance process
        '/screenshots/apple-developer-portal.png', // Step 8: Verify account status
      ]
    },
    relatedQuestions: ['apple-developer-setup', 'account-compliance', 'agreement-terms'],
    lastUpdated: '2025-09-03'
  },
  {
    id: 'duns-number-setup',
    question: 'How do I obtain and set up a DUNS number for my Apple Developer account?',
    answer: 'A DUNS (Data Universal Numbering System) number is required for Apple Developer Program enrollment for organizations. This unique identifier helps verify your business entity.',
    category: 'third-party',
    difficulty: 'advanced',
    estimatedTime: '15-30 minutes',
    tags: ['duns', 'apple', 'developer', 'business', 'verification', 'advanced'],
    walkthrough: {
      title: 'DUNS Number Setup for Apple Developer',
      description: 'Complete guide for obtaining and setting up a DUNS number with step-by-step screenshots.',
      steps: [
        'Visit the D&B (Dun & Bradstreet) website at dnb.com',
        'Search for your business using your business name and address',
        'If your business is not found, register for a new DUNS number',
        'Complete the business registration form with accurate information',
        'Provide required business documentation and verification',
        'Wait for D&B to process and assign your DUNS number (1-2 business days)',
        'Log into your Apple Developer account',
        'Enter your DUNS number in the Apple Developer Program enrollment',
        'Verify the DUNS number matches your business information'
      ],
      screenshots: [
        '/screenshots/dnb-homepage.png', // Step 1: D&B homepage
        '/screenshots/dnb-duns-lookup.png', // Step 2: Business search
        '/screenshots/dnb-getstarted.png', // Step 3: Register for new DUNS
        '/screenshots/dnb-request-form.png', // Step 4: Registration form
        '/screenshots/dnb-request-reqdocs.png', // Step 5: Required documentation
        '/screenshots/dnb-request-check.png', // Step 6: Processing status
        '/screenshots/apple-developer-login.png', // Step 7: Apple Developer login
        '/screenshots/apple-developer-organization-enrollment.png', // Step 8: DUNS enrollment
        // Note: Step 9 (verify DUNS) uses same screenshot as step 8
      ]
    },
    relatedQuestions: ['apple-developer-setup', 'business-verification', 'organization-enrollment'],
    lastUpdated: '2025-09-03'
  },
  {
    id: 'apple-developer-signup',
    question: 'How do I sign up for an Apple Developer account?',
    answer: 'To submit apps to the App Store, you need an Apple Developer Program membership. **IMPORTANT: You must enroll as an Organization (business account), not as an Individual.** Individual accounts cannot submit apps through our platform.',
    category: 'third-party',
    difficulty: 'intermediate',
    estimatedTime: '20-30 minutes',
    tags: ['apple', 'developer', 'signup', 'business', 'organization', 'enrollment'],
    walkthrough: {
      title: 'Apple Developer Program Enrollment Guide',
      description: 'Step-by-step guide for enrolling in the Apple Developer Program as an Organization (Business Account). Note: Screenshots are available for initial steps and final access, but not for the detailed enrollment form process.',
      steps: [
        'Visit developer.apple.com and click "Account"',
        'Sign in with your Apple ID (or create one if needed)',
        'Click "Enroll" in the Apple Developer Program section',
        '**CRITICAL: Select "Organization" as your entity type (NOT Individual)**',
        'Enter your organization\'s legal business name exactly as it appears on official documents',
        'Provide your organization\'s DUNS number (required for business accounts)',
        'Enter your organization\'s address and contact information',
        'Designate yourself as the Team Agent (primary contact)',
        'Review and accept the Apple Developer Program License Agreement',
        'Complete the payment process ($99/year for organization accounts)',
        'Wait for Apple to verify your organization (1-2 business days)',
        'Once approved, you\'ll receive confirmation and can access the developer portal'
      ],
      screenshots: [
        '/screenshots/apple-developer-homepage.png', // Step 1: Apple Developer homepage
        '/screenshots/apple-developer-login.png', // Step 2: Apple ID sign in
        '/screenshots/apple-developer-enroll-button.png', // Step 3: Enroll button
        '/screenshots/apple-developer-organization-enrollment.png', // Step 4: Organization selection
        // Note: No screenshots available for steps 5-11 (enrollment form details, payment, verification)
        '/screenshots/apple-developer-portal.png', // Step 12: Developer portal access
      ]
    },
    relatedQuestions: ['apple-developer-setup', 'duns-number-setup', 'business-verification'],
    lastUpdated: '2025-09-03'
  }
];

export function FAQPage() {
  const { jiraKey } = useParams<{ jiraKey: string }>();
  
  // Determine the correct back URL based on the current route
  const getBackUrl = () => {
    if (jiraKey) {
      // If we're on a project-specific help page, go back to the project
      return `/project/${jiraKey}`;
    } else {
      // If we're on the general help page, go to auth (since there's no general portal)
      return '/auth';
    }
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [activeWalkthrough, setActiveWalkthrough] = useState<string | null>(null);
  const [walkthroughStep, setWalkthroughStep] = useState(0);
  const [copiedArticleId, setCopiedArticleId] = useState<string | null>(null);

  const filteredFAQs = useMemo(() => {
    return faqData.filter(faq => {
      const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           faq.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
      const matchesDifficulty = selectedDifficulty === 'all' || faq.difficulty === selectedDifficulty;
      
      return matchesSearch && matchesCategory && matchesDifficulty;
    });
  }, [searchTerm, selectedCategory, selectedDifficulty]);

  const setArticleHash = (id: string | null) => {
    const nextUrl = id
      ? `${window.location.pathname}${window.location.search}#${id}`
      : `${window.location.pathname}${window.location.search}`;
    // Preserve React Router history state (idx/key); null would wipe the stack entry.
    window.history.replaceState(window.history.state, '', nextUrl);
  };

  const expandAndScrollToArticle = (id: string) => {
    if (!faqData.some((faq) => faq.id === id)) {
      return;
    }
    // Clear filters so the target article is in the rendered list
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedDifficulty('all');
    setExpandedItems(new Set([id]));
    setArticleHash(id);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  };

  useEffect(() => {
    const openFromHash = () => {
      const hashId = window.location.hash.replace(/^#/, '');
      if (!hashId) {
        return;
      }
      expandAndScrollToArticle(hashId);
    };

    openFromHash();
    window.addEventListener('hashchange', openFromHash);
    return () => window.removeEventListener('hashchange', openFromHash);
    // faqData is module-scoped; openFromHash uses stable setState + that constant
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
      if (window.location.hash.replace(/^#/, '') === id) {
        setArticleHash(null);
      }
    } else {
      newExpanded.add(id);
      setArticleHash(id);
    }
    setExpandedItems(newExpanded);
  };

  const copyArticleLink = async (id: string) => {
    const shareUrl = `${window.location.origin}${window.location.pathname}${window.location.search}#${id}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedArticleId(id);
      setArticleHash(id);
      setExpandedItems((prev) => new Set(prev).add(id));
      window.setTimeout(() => {
        setCopiedArticleId((current) => (current === id ? null : current));
      }, 2000);
    } catch {
      // Fallback for older browsers / denied clipboard permission
      window.prompt('Copy this article link:', shareUrl);
    }
  };

  const startWalkthrough = (id: string) => {
    setActiveWalkthrough(id);
    setWalkthroughStep(0);
  };

  const nextWalkthroughStep = () => {
    const faq = faqData.find(f => f.id === activeWalkthrough);
    if (faq?.walkthrough && walkthroughStep < faq.walkthrough.steps.length - 1) {
      setWalkthroughStep(walkthroughStep + 1);
    }
  };

  const prevWalkthroughStep = () => {
    if (walkthroughStep > 0) {
      setWalkthroughStep(walkthroughStep - 1);
    }
  };

  const closeWalkthrough = () => {
    setActiveWalkthrough(null);
    setWalkthroughStep(0);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'setup': return <Settings className="w-4 h-4" />;
      case 'technical': return <Zap className="w-4 h-4" />;
      case 'customization': return <Sparkles className="w-4 h-4" />;
      case 'troubleshooting': return <AlertCircle className="w-4 h-4" />;
      case 'general': return <HelpCircle className="w-4 h-4" />;
      case 'third-party': return <ExternalLink className="w-4 h-4" />;
      default: return <BookOpen className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <Logo className="h-8 w-auto" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Help Center</h1>
                <p className="text-sm text-gray-600">Find answers to common questions and setup guides</p>
            </div>
          </div>
            <Link
              to={getBackUrl()}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {jiraKey ? 'Back to Project' : 'Back to Portal'}
            </Link>
        </div>
      </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8 sticky top-0 z-10">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                  placeholder="Search questions, answers, or tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              </div>
            </div>

            {/* Category Filter */}
            <div className="lg:w-48">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Categories</option>
                <option value="setup">Setup</option>
                <option value="technical">Technical</option>
                <option value="customization">Customization</option>
                <option value="troubleshooting">Troubleshooting</option>
                <option value="general">General</option>
                <option value="third-party">Third Party</option>
              </select>
            </div>

            {/* Difficulty Filter */}
            <div className="lg:w-48">
              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Difficulties</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredFAQs.length} of {faqData.length} questions
          </div>
        </div>

        {/* FAQ Items */}
        <div className="space-y-6">
          {filteredFAQs.map((faq) => (
            <div id={faq.id} key={faq.id} className="bg-white rounded-lg shadow-sm border overflow-hidden scroll-mt-56 lg:scroll-mt-40">
              {/* FAQ Header */}
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="flex items-center space-x-2">
                        {getCategoryIcon(faq.category)}
                        <span className="text-sm font-medium text-gray-600 capitalize">
                          {faq.category.replace('-', ' ')}
                        </span>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDifficultyColor(faq.difficulty)}`}>
                        {faq.difficulty}
                      </span>
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock className="w-4 h-4 mr-1" />
                        {faq.estimatedTime}
                      </div>
                    </div>
                    
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {faq.question}
                    </h3>
                    
                    <p className="text-gray-600 mb-4">
                      {faq.answer}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {faq.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Last Updated */}
                    {faq.lastUpdated && (
                      <div className="text-sm text-gray-500 mb-4">
                        Last updated: {faq.lastUpdated}
                      </div>
                    )}
                  </div>

              <button
                    onClick={() => toggleExpanded(faq.id)}
                    className="ml-4 flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-expanded={expandedItems.has(faq.id)}
                    aria-label={expandedItems.has(faq.id) ? 'Collapse article' : 'Expand article'}
              >
                    {expandedItems.has(faq.id) ? (
                      <ChevronUp className="w-5 h-5" />
                ) : (
                      <ChevronDown className="w-5 h-5" />
                )}
              </button>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={() => copyArticleLink(faq.id)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    aria-label={`Copy link to ${faq.question}`}
                  >
                    {copiedArticleId === faq.id ? (
                      <>
                        <Check className="w-4 h-4 mr-2 text-green-600" />
                        Link copied
                      </>
                    ) : (
                      <>
                        <Link2 className="w-4 h-4 mr-2" />
                        Copy link
                      </>
                    )}
                  </button>
                  {faq.walkthrough && (
                    <button
                      onClick={() => startWalkthrough(faq.id)}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Start Walkthrough
                    </button>
                  )}
                  
                  <button
                    onClick={() => toggleExpanded(faq.id)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    {expandedItems.has(faq.id) ? 'Show Less' : 'Show More'}
                  </button>
                </div>
                        </div>
                        
              {/* Expanded Content */}
              {expandedItems.has(faq.id) && (
                <div className="border-t bg-gray-50 p-6">
                  {/* Walkthrough */}
                  {faq.walkthrough && (
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">
                        {faq.walkthrough.title}
                      </h4>
                      <p className="text-gray-600 mb-4">
                        {faq.walkthrough.description}
                      </p>
                      
                      <div className="bg-white rounded-lg border p-4">
                        <h5 className="font-medium text-gray-900 mb-3">Steps:</h5>
                        <ol className="list-decimal list-inside space-y-2 text-gray-700">
                          {faq.walkthrough.steps.map((step, index) => (
                            <li key={index} className="pl-2">{step}</li>
                          ))}
                        </ol>
                      </div>

                      {/* Screenshots */}
                      {faq.walkthrough.screenshots && faq.walkthrough.screenshots.length > 0 && (
                        <div className="mt-4">
                          <h5 className="font-medium text-gray-900 mb-3">Screenshots:</h5>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {faq.walkthrough.screenshots.map((screenshot, index) => (
                              <div key={index} className="bg-white rounded-lg border p-3">
                                              <img
                                                src={screenshot}
                                  alt={`Step ${index + 1} screenshot`}
                                  className="w-full h-32 object-cover rounded border"
                                                onError={(e) => {
                                                  const target = e.target as HTMLImageElement;
                                    target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDIwMCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTI4IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik04MCA0OEgxMjBWODBIODBWNDhaIiBmaWxsPSIjRTVFN0VCIi8+CjxwYXRoIGQ9Ik05MCA1OEgxMTBWNzBIOTBWNThaIiBmaWxsPSIjOUNBM0FGIi8+Cjx0ZXh0IHg9IjEwMCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM2QjcyODAiIHRleHQtYW5jaG9yPSJtaWRkbGUiPlNjcmVlbnNob3Q8L3RleHQ+Cjx0ZXh0IHg9IjEwMCIgeT0iMTEwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTAiIGZpbGw9IiM5Q0EzQUYiIHRleHQtYW5jaG9yPSJtaWRkbGUiPm5vdCBhdmFpbGFibGU8L3RleHQ+Cjwvc3ZnPgo=';
                                    target.alt = 'Screenshot not available';
                                  }}
                                  onLoad={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'block';
                                  }}
                                />
                                <p className="text-xs text-gray-500 mt-2 text-center">
                                  Step {index + 1}
                                </p>
                                              </div>
                            ))}
                                            </div>
                                          </div>
                                        )}
                    </div>
                  )}

                  {/* Related Questions */}
                  {faq.relatedQuestions && faq.relatedQuestions.length > 0 && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">
                        Related Questions
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {faq.relatedQuestions.map((relatedId) => {
                          const relatedFAQ = faqData.find(f => f.id === relatedId);
                          if (!relatedFAQ) return null;
                          
                          return (
                            <button
                              key={relatedId}
                              onClick={() => expandAndScrollToArticle(relatedId)}
                              className="text-left p-3 bg-white rounded-lg border hover:border-blue-300 hover:bg-blue-50 transition-colors"
                            >
                              <p className="font-medium text-gray-900 text-sm">
                                {relatedFAQ.question}
                              </p>
                              <p className="text-gray-600 text-xs mt-1">
                                {relatedFAQ.estimatedTime} • {relatedFAQ.difficulty}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Enhanced Contact Support */}
        <div className="mt-16 bg-white rounded-lg shadow-sm border p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
              <MessageCircle className="h-6 w-6 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Still Need Help?
            </h2>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Can't find the answer you're looking for? Our support team is here to help you get your application up and running smoothly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:support@example.com"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Mail className="w-5 h-5 mr-2" />
                Email Support
              </a>
              <a
                href="tel:+1-555-0123"
                className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call Support
              </a>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              Available Monday - Friday, 9:00 AM - 6:00 PM EST
            </p>
                                      </div>
                                    </div>
                                  </div>
                                  
      {/* Walkthrough Modal */}
      {activeWalkthrough && (() => {
        const faq = faqData.find(f => f.id === activeWalkthrough);
        if (!faq?.walkthrough) return null;
        
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {faq.walkthrough.title}
                  </h2>
                  <button
                    onClick={closeWalkthrough}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <p className="text-gray-600 mb-6">
                  {faq.walkthrough.description}
                </p>

                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                    <span>Step {walkthroughStep + 1} of {faq.walkthrough.steps.length}</span>
                    <span>{Math.round(((walkthroughStep + 1) / faq.walkthrough.steps.length) * 100)}% Complete</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${((walkthroughStep + 1) / faq.walkthrough.steps.length) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Current Step */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-blue-900 mb-3">
                    Step {walkthroughStep + 1}: {faq.walkthrough.steps[walkthroughStep]}
                  </h3>
                  
                  {/* Screenshot for current step */}
                  {faq.walkthrough.screenshots && faq.walkthrough.screenshots[walkthroughStep] && (
                    <div className="mt-4">
                      <img
                        src={faq.walkthrough.screenshots[walkthroughStep]}
                        alt={`Step ${walkthroughStep + 1} screenshot`}
                        className="w-full max-w-md mx-auto rounded-lg border shadow-sm"
                                        onError={(e) => {
                                          const target = e.target as HTMLImageElement;
                                          target.style.display = 'none';
                        }}
                      />
                          </div>
                        )}
                      </div>

                {/* Navigation */}
                <div className="flex items-center justify-between">
                  <button
                    onClick={prevWalkthroughStep}
                    disabled={walkthroughStep === 0}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <SkipBack className="w-4 h-4 mr-2" />
                    Previous
                  </button>

                  <div className="flex items-center space-x-2">
                    {faq.walkthrough.steps.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setWalkthroughStep(index)}
                        className={`w-3 h-3 rounded-full transition-colors ${
                          index === walkthroughStep ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      />
          ))}
        </div>

                  <button
                    onClick={nextWalkthroughStep}
                    disabled={walkthroughStep === faq.walkthrough.steps.length - 1}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <SkipForward className="w-4 h-4 ml-2" />
                  </button>
              </div>

                {/* Complete Button */}
                {walkthroughStep === faq.walkthrough.steps.length - 1 && (
                  <div className="mt-6 text-center">
                    <button
                      onClick={closeWalkthrough}
                      className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Complete Walkthrough
                    </button>
            </div>
                )}
          </div>
        </div>
      </div>
        );
      })()}
    </div>
  );
}
